import {
  getCurrentScope,
  onScopeDispose,
  readonly,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

/** A debounced function: call it like the original, plus control over the pending call. */
export interface NxDebounced<A extends unknown[]> {
  (...args: A): void;
  /** Drops the pending call. */
  cancel(): void;
  /** Runs the pending call now (no-op when none is pending). */
  flush(): void;
  /** Whether a call is waiting. */
  pending(): boolean;
}

/**
 * Trailing debounce: `fn` runs `ms` after the last call, with that call's
 * arguments. `ms <= 0` runs synchronously. Timer-based and framework-agnostic;
 * inside components prefer {@link useDebouncedFn}, which cancels on unmount.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number
): NxDebounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let args: A | null = null;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  const run = () => {
    if (!args) return;
    const next = args;
    args = null;
    fn(...next);
  };

  const debounced = ((...next: A) => {
    args = next;
    if (ms <= 0) {
      clear();
      run();
      return;
    }
    clear();
    timer = setTimeout(() => {
      timer = null;
      run();
    }, ms);
  }) as NxDebounced<A>;
  debounced.cancel = () => {
    clear();
    args = null;
  };
  debounced.flush = () => {
    clear();
    run();
  };
  debounced.pending = () => args !== null;
  return debounced;
}

/** A throttled function plus a way to drop the trailing call. */
export interface NxThrottled<A extends unknown[]> {
  (...args: A): void;
  /** Drops a waiting trailing call and ends the current window. */
  cancel(): void;
}

export interface NxThrottleOptions {
  /** Run on the first call of a window. Default `true`. */
  leading?: boolean;
  /** Run once more at the end of a window if it was called meanwhile. Default `true`. */
  trailing?: boolean;
}

/**
 * Runs `fn` at most once per `ms`. With the defaults the first call runs at
 * once and calls during the window collapse into one trailing run with the
 * latest arguments. Unlike the kit-internal scroll throttle, the trailing run
 * only happens when something was called during the window.
 */
export function throttle<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
  options: NxThrottleOptions = {}
): NxThrottled<A> {
  const leading = options.leading ?? true;
  const trailing = options.trailing ?? true;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let waiting: A | null = null;

  const startWindow = () => {
    timer = setTimeout(() => {
      timer = null;
      if (trailing && waiting) {
        const next = waiting;
        waiting = null;
        fn(...next);
        startWindow();
      }
    }, ms);
  };

  const throttled = ((...args: A) => {
    if (timer === null) {
      if (leading) fn(...args);
      else waiting = args;
      startWindow();
      return;
    }
    waiting = args;
  }) as NxThrottled<A>;
  throttled.cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    waiting = null;
  };
  return throttled;
}

/** {@link debounce} that is cancelled when the calling effect scope ends (unmount). */
export function useDebouncedFn<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number
): NxDebounced<A> {
  const debounced = debounce(fn, ms);
  if (getCurrentScope()) onScopeDispose(debounced.cancel);
  return debounced;
}

/**
 * A read-only ref that follows `source` once it has been quiet for `ms` — the
 * search box pattern (`const query = useDebouncedRef(input, 300)`). A pending
 * update is dropped when the calling effect scope ends.
 */
export function useDebouncedRef<T>(source: MaybeRefOrGetter<T>, ms: number): Readonly<Ref<T>> {
  const value = shallowRef(toValue(source)) as Ref<T>;
  const update = debounce((next: T) => {
    value.value = next;
  }, ms);
  const stop = watch(() => toValue(source), update, { deep: true });
  if (getCurrentScope()) {
    onScopeDispose(() => {
      update.cancel();
      stop();
    });
  }
  return readonly(value) as Readonly<Ref<T>>;
}
